import React, { useEffect, useState, useCallback } from "react";
import { personalInfo, competencies, proudOf, patent, achievements, industryExpertise } from "../../mockData";
import { useScrollAnimation } from "../../hooks/useScrollAnimation";
import {
  Target, Cpu, BarChart3, Users, Shield, Lightbulb,
  Mail, Linkedin, ChevronDown, Award, Heart, ArrowRight, Github,
  TrendingUp, Zap
} from "lucide-react";
import { Badge } from "../ui/badge";

const iconMap = { Target, Cpu, BarChart3, Users, Shield, Lightbulb };

/* Floating particles for hero */
const Particles = () => (
  <>
    {[1,2,3,4,5,6,7,8].map(i => (
      <div key={i} className={`particle particle-${i}`} />
    ))}
  </>
);

/* Data stream columns */
const DataStreams = () => {
  const streams = [
    { left: '5%', delay: '0s', content: '01001 SBOM SPDX 10110 CVE 01' },
    { left: '15%', delay: '3s', content: 'TPM DM-Verity SecureBoot 110' },
    { left: '85%', delay: '5s', content: 'Yocto iMX8 AppArmor 01100' },
    { left: '92%', delay: '8s', content: 'FHIR HL7 IoMT Docker Azure' },
  ];
  return (
    <>
      {streams.map((s, i) => (
        <div key={i} className="data-stream" style={{ left: s.left, animationDelay: s.delay }}>
          {s.content}
        </div>
      ))}
    </>
  );
};

/* Typewriter hook */
const useTypewriter = (texts, speed = 80, pause = 2000) => {
  const [display, setDisplay] = useState('');
  const [textIndex, setTextIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const current = texts[textIndex];
    const timeout = setTimeout(() => {
      if (!isDeleting) {
        setDisplay(current.substring(0, charIndex + 1));
        setCharIndex(prev => prev + 1);
        if (charIndex + 1 === current.length) {
          setTimeout(() => setIsDeleting(true), pause);
        }
      } else {
        setDisplay(current.substring(0, charIndex - 1));
        setCharIndex(prev => prev - 1);
        if (charIndex - 1 === 0) {
          setIsDeleting(false);
          setTextIndex((prev) => (prev + 1) % texts.length);
        }
      }
    }, isDeleting ? speed / 2 : speed);
    return () => clearTimeout(timeout);
  }, [charIndex, isDeleting, textIndex, texts, speed, pause]);

  return display;
};

const AnimatedStat = ({ value, label, description, delay }) => {
  const [ref, isVisible] = useScrollAnimation();
  return (
    <div
      ref={ref}
      className={`fade-in-up ${isVisible ? "visible" : ""} text-center`}
      style={{ transitionDelay: `${delay}s` }}
    >
      <div className="text-4xl md:text-5xl font-bold gradient-text-animated font-mono breathe">{value}</div>
      <div className="text-sm font-semibold text-slate-200 mt-2">{label}</div>
      <div className="text-xs text-slate-500 mt-1">{description}</div>
    </div>
  );
};

const HeroSection = () => {
  const [loaded, setLoaded] = useState(false);
  useEffect(() => { setLoaded(true); }, []);

  const typedRole = useTypewriter([
    'Healthcare Platforms',
    'Cybersecurity & SBOM',
    'Embedded Linux Systems',
    'Defence in Depth',
    'IoMT & Edge Computing'
  ], 70, 1800);

  return (
    <section id="hero" className="hero-bg grid-bg min-h-screen flex items-center justify-center relative pt-20">
      {/* Floating particles */}
      <Particles />
      {/* Data stream effect */}
      <DataStreams />

      <div className="max-w-7xl mx-auto px-6 py-20 w-full relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
          {/* Left Content */}
          <div className={`flex-1 space-y-6 transition-all duration-1000 ${loaded ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="flex items-center gap-3">
              <div className="h-px w-12 bg-cyan-500/50"></div>
              <span className="text-cyan-400 text-sm font-mono font-medium tracking-wider uppercase">Technology Executive</span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
              <span className="text-slate-100 neon-text">Abraham</span><br />
              <span className="gradient-text-animated">Gogulamudi</span>
            </h1>
            <p className="text-lg md:text-xl text-slate-400 leading-relaxed max-w-xl">
              Senior Engineering Manager driving{" "}
              <span className="text-cyan-400 font-medium">{typedRole}</span>
              <span className="typewriter-cursor"></span>
            </p>
            <p className="text-slate-500 text-sm max-w-lg leading-relaxed">
              Architecting the PCS medOS platform at GE HealthCare — a Class C Yocto-based OS
              enabling IoMT devices across Surgical, Observation, and Critical Care. 6+ products
              delivered, 40% cost reduction through platform consolidation.
            </p>
            <div className="flex items-center gap-4 pt-2">
              <a
                href="#contact"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
                }}
                className="btn-glow inline-flex items-center gap-2 bg-cyan-500 hover:bg-cyan-400 text-slate-900 font-semibold px-6 py-3 rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-cyan-500/25 text-sm relative"
              >
                <Mail size={16} /> Get in Touch
              </a>
              <a
                href={personalInfo.contact.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border border-slate-700 hover:border-cyan-500/50 text-slate-300 hover:text-cyan-400 px-6 py-3 rounded-lg transition-all duration-300 text-sm hover:shadow-lg hover:shadow-cyan-500/10"
              >
                <Linkedin size={16} /> LinkedIn
              </a>
              <a
                href={personalInfo.contact.github}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 border border-slate-700 hover:border-cyan-500/50 text-slate-300 hover:text-cyan-400 px-4 py-3 rounded-lg transition-all duration-300 text-sm hover:shadow-lg hover:shadow-cyan-500/10"
              >
                <Github size={16} />
              </a>
            </div>
          </div>

          {/* Photo with animated ring */}
          <div className={`flex-shrink-0 transition-all duration-1000 delay-300 ${loaded ? "opacity-100 scale-100" : "opacity-0 scale-95"}`}>
            <div className="relative">
              {/* Animated rotating border ring */}
              <div className="photo-ring"></div>
              <div className="w-64 h-64 md:w-80 md:h-80 rounded-2xl overflow-hidden border-2 border-slate-700/50 glow-cyan scan-line relative">
                <img
                  src={personalInfo.photo}
                  alt={personalInfo.name}
                  className="w-full h-full object-cover object-top"
                  onError={(e) => {
                    const img = e.target;
                    if (!(img instanceof HTMLImageElement)) return;
                    img.style.display = "none";
                    const fallback = img.nextElementSibling;
                    if (fallback instanceof HTMLElement) fallback.style.display = "flex";
                  }}
                />
                <div className="w-full h-full bg-gradient-to-br from-cyan-900/30 to-slate-900 items-center justify-center hidden">
                  <span className="text-6xl font-bold text-cyan-400 font-mono">AG</span>
                </div>
              </div>
              <div className="absolute -bottom-3 -right-3 bg-[#111827] border border-slate-700 rounded-lg px-4 py-2 flex items-center gap-2 border-glow">
                <div className="w-2 h-2 rounded-full bg-emerald-400 pulse-dot"></div>
                <span className="text-xs text-slate-400">Open to opportunities</span>
              </div>
              {/* Subtle glow behind photo */}
              <div className="absolute -inset-4 bg-cyan-500/5 rounded-3xl blur-xl -z-10"></div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="mt-20 grid grid-cols-3 gap-8 max-w-2xl mx-auto">
          {personalInfo.stats.map((stat, i) => (
            <AnimatedStat key={i} {...stat} delay={i * 0.15} />
          ))}
        </div>

        <div className="flex justify-center mt-16">
          <ChevronDown className="text-slate-600 animate-bounce" size={24} />
        </div>
      </div>
    </section>
  );
};

const AboutSection = () => {
  const [ref, isVisible] = useScrollAnimation();
  const [ref2, isVisible2] = useScrollAnimation();

  return (
    <section id="about" className="py-24 relative">
      <div className="max-w-7xl mx-auto px-6">
        <div ref={ref} className={`fade-in-up ${isVisible ? "visible" : ""}`}>
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px w-12 bg-cyan-500/50"></div>
            <span className="text-cyan-400 text-sm font-mono font-medium tracking-wider uppercase">About</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-100 mb-8">
            Transforming Healthcare<br />Through <span className="gradient-text">Technology</span>
          </h2>
          <div className="grid md:grid-cols-2 gap-12 mb-16">
            <div className="space-y-4">
              <p className="text-slate-400 leading-relaxed">{personalInfo.detailedSummary}</p>
              <p className="text-slate-500 leading-relaxed text-sm">
                My passion lies in designing trustworthy systems — from secure boot to kernel
                hardening, SBOM-driven transparency, and vulnerability remediation. I actively
                engage with the open-source community, contributing to discussions around Linux
                in safety-critical and regulated domains, and collaborate across industry and
                silicon partners to improve security for edge and medical devices.
              </p>
            </div>
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-slate-200 flex items-center gap-2">
                <Heart size={18} className="text-cyan-400" /> What I'm Most Proud Of
              </h3>
              {proudOf.map((item, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 mt-2 flex-shrink-0"></div>
                  <p className="text-slate-400 text-sm">{item}</p>
                </div>
              ))}
              <div className="mt-6 p-4 glass-card rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <Award size={16} className="text-amber-400" />
                  <span className="text-sm font-semibold text-amber-400">Patent Holder</span>
                </div>
                <p className="text-slate-400 text-xs">{patent.title}</p>
                <p className="text-slate-600 text-xs mt-1 font-mono">{patent.number}</p>
              </div>
            </div>
          </div>

          {/* Achievements & Impact */}
          <div ref={ref2} className={`fade-in-up ${isVisible2 ? "visible" : ""}`}>
            <h3 className="text-lg font-semibold text-slate-200 mb-6 flex items-center gap-2">
              <TrendingUp size={18} className="text-cyan-400" /> Achievements & Impact
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-16">
              {achievements.map((a, i) => (
                <div key={i} className="glass-card tilt-card rounded-xl p-5 text-center">
                  <div className="text-2xl md:text-3xl font-bold gradient-text-animated font-mono breathe mb-1">{a.value}</div>
                  <div className="text-sm font-semibold text-slate-200 mb-2">{a.label}</div>
                  <p className="text-slate-500 text-[11px] leading-relaxed">{a.description}</p>
                </div>
              ))}
            </div>

            {/* Industry Expertise */}
            <h3 className="text-lg font-semibold text-slate-200 mb-6 flex items-center gap-2">
              <Zap size={18} className="text-cyan-400" /> Industry Expertise
            </h3>
            <div className="grid md:grid-cols-2 gap-x-8 gap-y-3">
              {industryExpertise.map((exp, i) => (
                <div key={i} className="group">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm text-slate-300 font-medium">{exp.domain}</span>
                    <Badge className={`text-[10px] px-2 py-0 ${
                      exp.level === "Native"
                        ? "bg-cyan-500/15 text-cyan-400 border-cyan-500/30 hover:bg-cyan-500/20"
                        : exp.level === "Proficient"
                        ? "bg-amber-500/15 text-amber-400 border-amber-500/30 hover:bg-amber-500/20"
                        : "bg-slate-500/15 text-slate-400 border-slate-500/30 hover:bg-slate-500/20"
                    }`}>
                      {exp.level}
                    </Badge>
                  </div>
                  <div className="h-1.5 bg-slate-800/50 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-1000 ${
                        isVisible2 ? "opacity-100" : "opacity-0 w-0"
                      } ${
                        exp.level === "Native"
                          ? "bg-gradient-to-r from-cyan-500 to-cyan-400"
                          : exp.level === "Proficient"
                          ? "bg-gradient-to-r from-amber-500 to-amber-400"
                          : "bg-gradient-to-r from-slate-500 to-slate-400"
                      }`}
                      style={{ width: isVisible2 ? `${exp.percentage}%` : "0%" }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div className="section-divider mt-24 mx-auto max-w-7xl"></div>
    </section>
  );
};

const CompetenciesSection = () => {
  const [ref, isVisible] = useScrollAnimation();

  return (
    <section id="competencies" className="py-24 relative">
      <div className="max-w-7xl mx-auto px-6">
        <div ref={ref} className={`fade-in-up ${isVisible ? "visible" : ""}`}>
          <div className="flex items-center gap-3 mb-4">
            <div className="h-px w-12 bg-cyan-500/50"></div>
            <span className="text-cyan-400 text-sm font-mono font-medium tracking-wider uppercase">Core Competencies</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-100 mb-12">
            Technical <span className="gradient-text">Excellence</span>
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {competencies.map((comp, i) => {
            const Icon = iconMap[comp.icon];
            return (
              <div
                key={i}
                className={`glass-card tilt-card rounded-xl p-6 fade-in-up ${isVisible ? "visible" : ""} stagger-${i + 1}`}
              >
                <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center mb-4">
                  {Icon && <Icon size={20} className="text-cyan-400" />}
                </div>
                <h3 className="text-base font-semibold text-slate-200 mb-2">{comp.title}</h3>
                <p className="text-slate-500 text-sm leading-relaxed">{comp.description}</p>
              </div>
            );
          })}
        </div>
      </div>
      <div className="section-divider mt-24 mx-auto max-w-7xl"></div>
    </section>
  );
};

const HeroAboutCompetencies = () => (
  <>
    <HeroSection />
    <AboutSection />
    <CompetenciesSection />
  </>
);

export default HeroAboutCompetencies;
